/**
 * Fullscreen gallery viewer for vehicle-show Blade page — synthèse Ferrari Approved / JamesEdition /
 * CarJager, sur fond blanc :
 * - barre fixe : « ← Retour à l'annonce » (Ferrari, JamesEdition), titre centré + compteur
 *   (CarJager), bascule diaporama / mosaïque (Ferrari), CTA « Calculez votre mensualité »
 *   (le « Message » de JamesEdition) ;
 * - diaporama : une photo à la fois sans rognage, flèches ‹ ›, glissement au doigt (scroll-snap),
 *   bande de vignettes (CarJager) ;
 * - mosaïque éditoriale (2 grandes puis rangées de 3) + encart CTA en bas (JamesEdition).
 * Fermeture : « Retour », Échap, bouton Retour du navigateur (entrée d'historique). Le CTA ferme la
 * galerie puis déclenche le bouton de simulation de la fiche : le parcours reste inchangé.
 *
 * Mobile (≤ 48rem) : toucher la photo de la fiche ouvre la mosaïque, défilée jusqu'à la photo
 * touchée ; barre « Marque Modèle / N photos » et bouton jaune dans un bandeau bas, comme la fiche.
 */

import { carouselScrollLeft } from "./carousel-sync";
import { galleryHeading } from "./gallery-heading";
import { replayPendingTap } from "./pending-tap";
import { copyTrackingContext } from "../analytics/tracking-context";

type ViewerView = "single" | "grid";

let overlay: HTMLElement | null = null;
let previousFocus: HTMLElement | null = null;
let currentPhoto = 0;
let photoCount = 0;
let onResize: (() => void) | null = null;
/** Photo demandée par une navigation programmée, tant que le défilement n'est pas terminé. */
let scrollTarget: number | null = null;
let settleTimer: number | undefined;
/** Fermeture via l'interface en cours : on attend le retour d'historique avant toute réouverture. */
let closingViaHistory = false;
let afterHistoryClose: (() => void) | null = null;
let closeFallbackTimer: number | undefined;
/** Heure d'ouverture de la galerie (`performance.now()`), pour l'anti double toucher. */
let openedAt = 0;
/** Dernière ouverture : permet de rouvrir la galerie avec le bouton « Avancer » du navigateur. */
let lastOpen: {
    photos: ViewerPhoto[];
    title: string;
    index: number;
    view: ViewerView;
} | null = null;

interface ViewerPhoto {
    src: string;
    srcset: string;
    thumb: string;
}

const SINGLE_ICON =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="1.5" stroke="currentColor" stroke-width="2"/></svg>';
const GRID_ICON =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>';
const BACK_ICON =
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M19 12H5M11 6l-6 6 6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
// Mosaïque : 2 grandes tuiles (½ largeur) puis rangées de 3 ; une colonne pleine largeur sur mobile.
const MOSAIC_SIZES =
    "(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 640px";
/** Libellé du bandeau bas de la galerie mobile : celui du bandeau de la fiche. */
const MOBILE_CTA_LABEL = "Découvrir mon offre sur mesure";
/** Même seuil que le bloc mobile de `_gallery-viewer.scss` (`max-width: $breakpoint-m`). */
const MOBILE_VIEWER_QUERY = "(max-width: 48rem)";
/**
 * Juste après l'ouverture, un clic sur une tuile ou une vignette est ignoré : le 2e toucher d'un
 * double toucher sur la photo de la fiche tomberait sinon sur la mosaïque et ouvrirait le diaporama.
 */
const OPEN_TAP_GUARD_MS = 350;

function justOpened(event: MouseEvent): boolean {
    // Activation au clavier (Entrée, Espace : `detail` à 0) : jamais un double toucher.
    if (event.detail === 0) return false;
    return performance.now() - openedAt < OPEN_TAP_GUARD_MS;
}
const PREV_ICON =
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M15 6l-6 6 6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const NEXT_ICON =
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

/** Bouton « Découvrir mon offre sur mesure » visible sur la fiche (desktop : résumé ; mobile : barre fixe). */
function pageSimulateButton(): HTMLElement | null {
    const buttons = Array.from(
        document.querySelectorAll<HTMLElement>(".simulate-btn"),
    ).filter((btn) => !overlay?.contains(btn));
    return (
        buttons.find((btn) => btn.offsetParent !== null) ?? buttons[0] ?? null
    );
}

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

function imgTag(
    { src, srcset }: { src: string; srcset: string },
    sizes: string,
    alt: string,
    eager: boolean,
): string {
    return `<img src="${src}"${
        srcset ? ` srcset="${srcset}" sizes="${sizes}"` : ""
    } alt="${alt}" loading="${eager ? "eager" : "lazy"}" decoding="async" />`;
}

function createOverlay(photos: ViewerPhoto[], title: string): HTMLElement {
    const sizes =
        document.getElementById("gallery-photos")?.dataset.viewerSizes ?? "";
    const safeTitle = escapeHtml(title);
    // Titre façon carte véhicule : marque / modèle + finition / « 2023 · 75 312 km · 360 ch · Essence ».
    const data = document.getElementById("gallery-photos")?.dataset;
    const card = {
        make: escapeHtml(data?.cardMake ?? ""),
        model: escapeHtml(data?.cardModel || title),
        meta: escapeHtml(data?.cardMeta ?? ""),
    };
    // Libellé courant du bouton de la fiche (« Découvrir mon offre sur mesure » ou, une fois la
    // mensualité affichée, « Accédez aux détails de votre offre »). Pas de bouton = véhicule vendu.
    const pageCta = pageSimulateButton();
    const ctaLabel = pageCta
        ? escapeHtml(
              pageCta.textContent?.trim().replace(/\s+/g, " ") ||
                  "Découvrir mon offre sur mesure",
          )
        : "";
    const el = document.createElement("div");
    el.className = "viewer-photos";
    el.setAttribute("data-testid", "gallery-overlay");
    // Mesure PostHog : la fenêtre s'ouvre hors du conteneur de la fiche (fin de <body>), elle reprend
    // son contexte (véhicule, tranche de mensualité) pour que chaque clic le porte (docs/tracking-data-attr.md)
    copyTrackingContext(el);
    el.setAttribute("role", "dialog");
    el.setAttribute("aria-modal", "true");
    el.setAttribute("aria-label", `Photos — ${title}`);
    // Ouverture au toucher : le focus va sur la fenêtre elle-même, sans contour visible
    el.tabIndex = -1;
    el.innerHTML = `
        <div class="viewer-photos__bar">
            <button type="button" class="viewer-photos__close" data-testid="gallery-overlay-close" data-attr="vehicle-gallery-close" aria-label="Fermer la galerie et revenir à l'annonce">
                ${BACK_ICON}<span class="viewer-photos__close-label">Retour à l'annonce</span>
            </button>
            <div class="viewer-photos__heading">
                <p class="viewer-photos__title">${escapeHtml(
                    galleryHeading(
                        data?.cardMake ?? "",
                        data?.cardModel || title,
                    ),
                )}</p>
                <p class="viewer-photos__subtitle">${photos.length} photos</p>
                <p class="viewer-photos__brand">${
                    card.make
                }<span class="viewer-photos__counter-grid"> · ${
                    photos.length
                } photos</span></p>
                <p class="viewer-photos__model">${card.model}</p>
            </div>
            <div class="viewer-photos__actions">
                <div class="viewer-photos__views" role="group" aria-label="Affichage des photos">
                    <button type="button" class="viewer-photos__view" data-view="single" data-attr="vehicle-gallery-view" data-ph-capture-attribute-view="single" aria-pressed="true" aria-label="Diaporama">${SINGLE_ICON}</button>
                    <button type="button" class="viewer-photos__view" data-view="grid" data-attr="vehicle-gallery-view" data-ph-capture-attribute-view="grid" aria-pressed="false" aria-label="Mosaïque">${GRID_ICON}</button>
                </div>
                ${
                    ctaLabel
                        ? `<button type="button" class="button button--primary viewer-photos__cta" data-attr="vehicle-gallery-simulate" data-ph-capture-attribute-placement="top" aria-label="${ctaLabel}"><span class="viewer-photos__cta-long">${ctaLabel}</span><span class="viewer-photos__cta-short" aria-hidden="true">Ma mensualité</span></button>`
                        : ""
                }
            </div>
        </div>
        <div class="viewer-photos__stage">
            <button type="button" class="viewer-photos__nav viewer-photos__nav--prev" data-attr="vehicle-gallery-nav" data-ph-capture-attribute-direction="prev" aria-label="Photo précédente">${PREV_ICON}</button>
            <div class="viewer-photos__track" tabindex="-1">
                ${photos
                    .map(
                        (photo, i) =>
                            `<div class="viewer-photos__slide" data-index="${i}">${imgTag(
                                photo,
                                sizes,
                                `${safeTitle} - photo ${i + 1}`,
                                i < 2,
                            )}</div>`,
                    )
                    .join("")}
            </div>
            <button type="button" class="viewer-photos__nav viewer-photos__nav--next" data-attr="vehicle-gallery-nav" data-ph-capture-attribute-direction="next" aria-label="Photo suivante">${NEXT_ICON}</button>
            <p class="viewer-photos__status" aria-live="polite"></p>
            <span class="viewer-photos__badge" aria-hidden="true"><span class="viewer-photos__counter-current">1</span>/${
                photos.length
            }</span>
        </div>
        <div class="viewer-photos__thumbs">
            ${photos
                .map(
                    (photo, i) =>
                        `<button type="button" class="viewer-photos__thumb" data-index="${i}" data-attr="vehicle-gallery-thumb" data-ph-capture-attribute-view="single" aria-label="Photo ${
                            i + 1
                        }">${imgTag(
                            { src: photo.thumb || photo.src, srcset: "" },
                            "",
                            "",
                            false,
                        )}</button>`,
                )
                .join("")}
        </div>
        <div class="viewer-photos__grid">
            <div class="viewer-photos__tiles">
                ${photos
                    .map(
                        (photo, i) =>
                            `<button type="button" class="viewer-photos__tile" data-index="${i}" data-attr="vehicle-gallery-thumb" data-ph-capture-attribute-view="grid" aria-label="Voir la photo ${
                                i + 1
                            }">${imgTag(
                                photo,
                                MOSAIC_SIZES,
                                `${safeTitle} - photo ${i + 1}`,
                                false,
                            )}</button>`,
                    )
                    .join("")}
            </div>
            ${
                ctaLabel
                    ? `<div class="viewer-photos__footer">
                <p class="viewer-photos__brand">${card.make}</p>
                <p class="viewer-photos__model viewer-photos__model--large">${
                    card.model
                }</p>
                <span class="viewer-photos__accent" aria-hidden="true"></span>
                ${
                    card.meta
                        ? `<p class="viewer-photos__specs">${card.meta}</p>`
                        : ""
                }
                <p class="viewer-photos__footer-text">Ce véhicule vous plaît ? Découvrez votre mensualité en quelques clics.</p>
                <button type="button" class="button button--primary viewer-photos__cta" data-attr="vehicle-gallery-simulate" data-ph-capture-attribute-placement="grid-footer">${ctaLabel}</button>
            </div>`
                    : ""
            }
        </div>
        ${
            ctaLabel
                ? `<div class="viewer-photos__bottom">
            <button type="button" class="button button--primary viewer-photos__cta viewer-photos__cta--bottom" data-attr="vehicle-gallery-simulate" data-ph-capture-attribute-placement="bottom">${MOBILE_CTA_LABEL}</button>
        </div>`
                : ""
        }
    `;
    return el;
}

function viewerTrack(): HTMLElement | null {
    return overlay?.querySelector<HTMLElement>(".viewer-photos__track") ?? null;
}

function isGridView(): boolean {
    return overlay?.classList.contains("viewer-photos--grid") ?? false;
}

/** Met à jour compteur, flèches et vignette active pour la photo `index`. */
function syncCurrent(index: number): void {
    if (!overlay) return;
    currentPhoto = index;
    overlay
        .querySelectorAll(".viewer-photos__counter-current")
        .forEach((counter) => (counter.textContent = String(index + 1)));
    overlay
        .querySelector<HTMLButtonElement>(".viewer-photos__nav--prev")
        ?.toggleAttribute("disabled", index <= 0);
    overlay
        .querySelector<HTMLButtonElement>(".viewer-photos__nav--next")
        ?.toggleAttribute("disabled", index >= photoCount - 1);
    const status = overlay.querySelector(".viewer-photos__status");
    if (status) status.textContent = `Photo ${index + 1} sur ${photoCount}`;
    overlay
        .querySelectorAll<HTMLElement>(".viewer-photos__thumb")
        .forEach((thumb) => {
            const active = Number(thumb.dataset.index) === index;
            if (active) thumb.setAttribute("aria-current", "true");
            else thumb.removeAttribute("aria-current");
            if (active) {
                thumb.scrollIntoView({ block: "nearest", inline: "nearest" });
            }
        });
}

function goToPhoto(index: number, smooth = true): void {
    const track = viewerTrack();
    if (!track) return;
    const target = Math.min(Math.max(index, 0), photoCount - 1);
    // Tant que le défilement programmé court, les positions intermédiaires ne doivent pas
    // remplacer la photo demandée (clics rapides sur « suivant », redimensionnement).
    scrollTarget = smooth ? target : null;
    track.scrollTo({
        left: target * track.clientWidth,
        behavior: smooth ? "smooth" : "auto",
    });
    syncCurrent(target);
}

/** Bascule diaporama ↔ mosaïque ; depuis la mosaïque, on ouvre la photo choisie. */
function setView(view: ViewerView, photoIndex?: number): void {
    if (!overlay) return;
    overlay.classList.toggle("viewer-photos--grid", view === "grid");
    overlay
        .querySelectorAll<HTMLElement>(".viewer-photos__view")
        .forEach((btn) =>
            btn.setAttribute("aria-pressed", String(btn.dataset.view === view)),
        );
    if (view === "grid") {
        // « Photo seule » reprendra la photo touchée
        if (photoIndex !== undefined) currentPhoto = photoIndex;
        scrollGridTo(photoIndex ?? 0);
        return;
    }
    const index = photoIndex ?? currentPhoto;
    // Attendre la mise en page du diaporama avant de se placer sur la photo.
    requestAnimationFrame(() => goToPhoto(index, false));
}

/** Mosaïque : amène la photo `index` juste sous la barre (en haut pour la première). */
function scrollGridTo(index: number): void {
    if (!overlay) return;
    const tile = overlay.querySelector<HTMLElement>(
        `.viewer-photos__tile[data-index="${index}"]`,
    );
    if (!tile || index <= 0) {
        overlay.scrollTo({ top: 0 });
        return;
    }
    const bar = overlay.querySelector<HTMLElement>(".viewer-photos__bar");
    const top =
        tile.getBoundingClientRect().top -
        overlay.getBoundingClientRect().top +
        overlay.scrollTop -
        (bar?.offsetHeight ?? 0);
    overlay.scrollTo({ top: Math.max(0, top) });
}

function onViewerKeydown(e: KeyboardEvent): void {
    if (!overlay) return;
    const single = !isGridView();
    switch (e.key) {
        case "Escape":
            requestClose();
            break;
        case "ArrowRight":
        case "ArrowDown":
            if (!single) break;
            e.preventDefault();
            goToPhoto(currentPhoto + 1);
            break;
        case "ArrowLeft":
        case "ArrowUp":
            if (!single) break;
            e.preventDefault();
            goToPhoto(currentPhoto - 1);
            break;
        case "Home":
            if (!single) break;
            e.preventDefault();
            goToPhoto(0);
            break;
        case "End":
            if (!single) break;
            e.preventDefault();
            goToPhoto(photoCount - 1);
            break;
        case "Tab": {
            // Le focus reste dans la galerie : boucle sur ses boutons visibles.
            const focusables = Array.from(
                overlay.querySelectorAll<HTMLElement>("button"),
            ).filter(
                (btn) =>
                    btn.offsetParent !== null && !btn.hasAttribute("disabled"),
            );
            const pos = focusables.indexOf(
                document.activeElement as HTMLElement,
            );
            const next = e.shiftKey
                ? (pos <= 0 ? focusables.length : pos) - 1
                : (pos + 1) % focusables.length;
            e.preventDefault();
            focusables[next]?.focus();
            break;
        }
    }
}

/**
 * Écouteur d'historique unique (installé une fois) :
 * - fermeture via l'interface → exécute l'action différée (ex. lancer la simulation) ;
 * - Retour depuis la galerie → ferme ;
 * - Avancer vers une entrée « galerie » → rouvre la galerie telle qu'elle était.
 */
function onPopState(): void {
    if (closingViaHistory) {
        finishHistoryClose();
        return;
    }
    const wantsViewer = history.state?.galleryViewer === true;
    if (!wantsViewer && overlay) {
        closeViewer();
    } else if (wantsViewer && !overlay && lastOpen) {
        openViewer(
            lastOpen.photos,
            lastOpen.title,
            lastOpen.index,
            lastOpen.view,
            false,
        );
    }
}

function finishHistoryClose(): void {
    window.clearTimeout(closeFallbackTimer);
    closingViaHistory = false;
    const run = afterHistoryClose;
    afterHistoryClose = null;
    run?.();
}

function bindViewer(el: HTMLElement): void {
    el
        .querySelector(".viewer-photos__close")
        ?.addEventListener("click", () => requestClose());
    el.querySelectorAll<HTMLElement>(".viewer-photos__view").forEach((btn) =>
        btn.addEventListener("click", () =>
            setView(btn.dataset.view === "grid" ? "grid" : "single"),
        ),
    );
    el.querySelectorAll<HTMLElement>(".viewer-photos__cta").forEach((btn) =>
        btn.addEventListener("click", () =>
            requestClose(() => pageSimulateButton()?.click()),
        ),
    );
    el
        .querySelector(".viewer-photos__nav--prev")
        ?.addEventListener("click", () => goToPhoto(currentPhoto - 1));
    el
        .querySelector(".viewer-photos__nav--next")
        ?.addEventListener("click", () => goToPhoto(currentPhoto + 1));
    el.querySelectorAll<HTMLElement>(".viewer-photos__thumb").forEach((thumb) =>
        thumb.addEventListener("click", (event) => {
            if (justOpened(event)) return;
            goToPhoto(Number(thumb.dataset.index ?? 0));
        }),
    );
    el.querySelectorAll<HTMLElement>(".viewer-photos__tile").forEach((tile) =>
        tile.addEventListener("click", (event) => {
            if (justOpened(event)) return;
            setView("single", Number(tile.dataset.index ?? 0));
            // La tuile disparaît avec la mosaïque : le focus passe au diaporama.
            el.querySelector<HTMLElement>(".viewer-photos__track")?.focus({
                preventScroll: true,
            });
        }),
    );

    // Glissement au doigt / trackpad : le compteur suit la photo affichée.
    const track = el.querySelector<HTMLElement>(".viewer-photos__track");
    if (track) {
        let pending = false;
        track.addEventListener(
            "scroll",
            () => {
                // Fenêtre en train de se fermer (fondu) : ses défilements ne touchent plus à l'état
                if (pending || overlay !== el) return;
                pending = true;
                requestAnimationFrame(() => {
                    pending = false;
                    if (overlay !== el) return;
                    const w = track.clientWidth;
                    if (w <= 0) return;
                    const measured = Math.round(track.scrollLeft / w);
                    window.clearTimeout(settleTimer);
                    settleTimer = window.setTimeout(() => {
                        if (overlay !== el) return;
                        // Défilement terminé : la position réelle fait foi (le doigt a pu interrompre).
                        scrollTarget = null;
                        const settled = Math.round(
                            track.scrollLeft / track.clientWidth,
                        );
                        if (settled !== currentPhoto) syncCurrent(settled);
                    }, 150);
                    if (scrollTarget === null && measured !== currentPhoto) {
                        syncCurrent(measured);
                    }
                });
            },
            { passive: true },
        );
    }
}

function openViewer(
    photos: ViewerPhoto[],
    title: string,
    startIndex = 0,
    view: ViewerView = "single",
    pushHistory = true,
    fromPointer = false,
): void {
    if (overlay || closingViaHistory) return;
    lastOpen = { photos, title, index: startIndex, view };
    openedAt = performance.now();

    previousFocus =
        document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
    photoCount = photos.length;
    currentPhoto = 0;
    overlay = createOverlay(photos, title);
    bindViewer(overlay);
    document.body.appendChild(overlay);
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onViewerKeydown);

    // Garder la photo courante cadrée si la fenêtre change de taille (rotation mobile).
    onResize = () => {
        if (!isGridView()) goToPhoto(currentPhoto, false);
    };
    window.addEventListener("resize", onResize);

    // Le bouton Retour du navigateur ferme la galerie au lieu de quitter la fiche.
    if (pushHistory) {
        history.pushState(
            { ...(history.state ?? {}), galleryViewer: true },
            "",
        );
    }

    setView(view, startIndex);
    if (view === "single") syncCurrent(startIndex);
    // Au clavier, focus sur « Retour » (contour :focus-visible) ; au toucher ou à la souris, sur la
    // fenêtre : Safari afficherait sinon un contour sur « Retour » sans action clavier.
    const focusTarget = fromPointer
        ? overlay
        : overlay.querySelector<HTMLElement>(".viewer-photos__close");
    focusTarget?.focus({ preventScroll: true });
}

/**
 * Ferme via l'interface : consomme l'entrée d'historique de la galerie (`history.back()`).
 * `afterClose` (ex. lancer la simulation) ne s'exécute qu'une fois ce retour appliqué, pour ne
 * pas être annulé par lui ; la galerie ne peut pas être rouverte entre-temps.
 */
function requestClose(afterClose?: () => void): void {
    if (!overlay) return;
    const onViewerEntry = history.state?.galleryViewer === true;
    closeViewer();
    if (!onViewerEntry) {
        afterClose?.();
        return;
    }
    closingViaHistory = true;
    afterHistoryClose = afterClose ?? null;
    // Filet de sécurité si le navigateur ne signale jamais le retour.
    closeFallbackTimer = window.setTimeout(finishHistoryClose, 1500);
    history.back();
}

function closeViewer(): void {
    if (!overlay) return;
    // Mémorise la position pour une réouverture via « Avancer ».
    if (lastOpen) {
        lastOpen.index = currentPhoto;
        lastOpen.view = isGridView() ? "grid" : "single";
    }
    document.body.style.overflow = "";
    document.removeEventListener("keydown", onViewerKeydown);
    window.clearTimeout(settleTimer);
    scrollTarget = null;
    if (onResize) window.removeEventListener("resize", onResize);
    onResize = null;
    fadeOutViewer(overlay);
    overlay = null;
    syncMobileCarousel(currentPhoto);
    previousFocus?.focus({ preventScroll: true });
    previousFocus = null;
}

/** Durée du fondu de sortie de la galerie, égale à celle de _gallery-viewer.scss. */
export const VIEWER_EXIT_MS = 150;

/**
 * Sortie de la galerie : fondu court (150 ms), sans bloquer la page. La fenêtre n'est plus « la »
 * galerie (une nouvelle peut s'ouvrir), ne reçoit plus de clic et quitte la page à la fin du fondu.
 */
function fadeOutViewer(el: HTMLElement): void {
    el.classList.add("viewer-photos--closing");
    el.setAttribute("aria-hidden", "true");
    el.removeAttribute("data-testid");
    window.setTimeout(() => el.remove(), VIEWER_EXIT_MS);
}

/**
 * À la fermeture de la galerie, le carrousel mobile de la fiche montre la dernière photo vue, d'un coup
 * (sans défilement animé : la piste a `scroll-behavior: smooth`). Rien sur ordinateur (carrousel masqué).
 */
function syncMobileCarousel(index: number): void {
    const track = document.getElementById("gallery-mobile-track");
    if (!track) return;
    const left = carouselScrollLeft(
        index,
        track.children.length,
        track.clientWidth,
        track.scrollLeft,
    );
    if (left !== null) track.scrollTo({ left, behavior: "instant" });
}

function getPhotos(): ViewerPhoto[] {
    const container = document.getElementById("gallery-photos");
    if (!container) return [];
    return Array.from(container.querySelectorAll("span"))
        .map((s) => ({
            src: s.dataset.viewerSrc ?? s.textContent?.trim() ?? "",
            srcset: s.dataset.viewerSrcset ?? "",
            thumb: s.dataset.thumbSrc ?? "",
        }))
        .filter((photo) => photo.src !== "");
}

function initMobileCarousel(photos: ViewerPhoto[], title: string): void {
    const carousel = document.getElementById("gallery-mobile-carousel");
    if (!carousel || photos.length === 0) return;

    const track = document.getElementById("gallery-mobile-track");
    const counterCurrent = document.querySelector<HTMLElement>(
        "#gallery-mobile-counter .gallery__photo-counter-current",
    );
    if (track && counterCurrent && photos.length > 1) {
        const updateCounter = (): void => {
            const w = track.clientWidth;
            if (w <= 0) return;
            const idx = Math.min(
                Math.round(track.scrollLeft / w),
                photos.length - 1,
            );
            counterCurrent.textContent = String(idx + 1);
        };
        track.addEventListener("scroll", updateCounter, { passive: true });
        window.addEventListener("resize", updateCounter);
        // Carrousel déjà glissé avant le chargement de ce module : le compteur rattrape la photo affichée.
        updateCounter();
    }

    carousel
        .querySelectorAll<HTMLImageElement>(".gallery-swiper-slide img")
        .forEach((img, index) => {
            // Mobile : la mosaïque d'abord, défilée jusqu'à la photo touchée
            img.addEventListener("click", () =>
                openViewer(
                    photos,
                    title,
                    index,
                    window.matchMedia(MOBILE_VIEWER_QUERY).matches
                        ? "grid"
                        : "single",
                    true,
                    true,
                ),
            );
        });
}

function initGallery(): void {
    const gallery = document.getElementById("vehicle-gallery");
    if (!gallery) return;

    const container = gallery.closest(".gallery-container");
    const photos = getPhotos();
    const title =
        document
            .querySelector<HTMLHeadingElement>(".vehicle-show h1")
            ?.textContent?.trim() ?? "";

    if (container?.classList.contains("gallery-sold")) {
        return;
    }

    if (photos.length === 0) return;

    initMobileCarousel(photos, title);
    window.addEventListener("popstate", onPopState);

    // Photo principale → diaporama ; pastille « Voir les N photos » → mosaïque (JamesEdition).
    const mainBtn = document.getElementById("gallery-main-btn");
    mainBtn?.addEventListener("click", (e) => {
        const onPill = (e.target as HTMLElement).closest(
            ".gallery-expand-pill",
        );
        openViewer(photos, title, 0, onPill ? "grid" : "single");
    });

    gallery
        .querySelectorAll<HTMLElement>("[data-gallery-thumb]")
        .forEach((thumb, index) => {
            thumb.addEventListener("click", () =>
                openViewer(photos, title, index + 1),
            );
        });

    const showAllBtn = document.getElementById("gallery-show-all");
    showAllBtn?.addEventListener("click", () =>
        openViewer(photos, title, 0, "grid"),
    );
}

function init(): void {
    initGallery();
    // Photo touchée avant le chargement de ce module : on rejoue l'appui, une seule fois.
    replayPendingTap("gallery");
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
} else {
    init();
}

export {};
